<template>
    <Head title="Redemption Tracker | Settings" />
    <div data-page="settings">
        <!-- Page header -->
        <div class="sm:flex sm:justify-between sm:items-center mb-10">
            <div class="mb-4 sm:mb-0">
                <h1 class="text-2xl md:text-3xl text-gray-800 dark:text-gray-100 font-bold">
                    Account settings
                </h1>
            </div>
        </div>

        <!-- TABLE -->
        <div
            class="lg:bg-white dark:bg-slate-800 dark:lg:bg-slate-800 lg:shadow-lg dark:lg:shadow-slate-900/50 lg:rounded-sm lg:border lg:border-gray-200 dark:lg:border-slate-700 mb-8"
        >
            <div class="flex flex-col lg:flex-row">
                <div
                    class="flex flex-nowrap p-0 lg:px-5 lg:py-10 bg-white dark:bg-slate-800 shadow-lg dark:shadow-slate-900/50 rounded-sm border border-gray-200 dark:border-slate-700 mb-8 lg:border-none lg:shadow-none"
                >
                    <SettingsNav />
                </div>

                <div
                    class="p-5 lg:px-0 lg:py-0 bg-white dark:bg-slate-800 shadow-lg dark:shadow-slate-900/50 rounded-sm border border-gray-200 dark:border-slate-700 mb-8 lg:mb-0 lg:border-r-0 lg:border-x-0 lg:border-y-0 lg:!border-l lg:!border-gray-200 dark:lg:!border-slate-700 lg:shadow-none"
                    style="grow: 1"
                >
                    <div class="flex flex-nowrap flex-col lg:px-10 lg:py-10">
                        <h3
                            class="text-lg font-semibold leading-7 text-slate-800 dark:text-slate-100"
                        >
                            Redemption tracker
                        </h3>

                        <!-- Manage sender emails section -->
                        <div class="mt-8">
                            <h4
                                class="text-base font-medium leading-6 text-slate-800 dark:text-slate-100"
                            >
                                Manage sender emails
                            </h4>
                            <p
                                class="text-sm font-normal leading-5 text-slate-500 dark:text-slate-400 mt-1"
                            >
                                Email addresses that can forward redemptions to
                                your account
                            </p>
                            <p
                                class="text-sm font-normal leading-5 text-slate-500 dark:text-slate-400 mt-1"
                            >
                                Note: You cannot add another My10x account
                                email as a sender.
                            </p>
                        </div>

                        <!-- Email list -->
                        <div class="mt-3">
                            <!-- Primary email -->
                            <div
                                class="flex py-3 px-0 items-center gap-3 self-stretch"
                            >
                                <svg
                                    width="20"
                                    height="20"
                                    viewBox="0 0 20 20"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                    class="shrink-0 text-slate-600 dark:text-slate-400"
                                >
                                    <path
                                        fill-rule="evenodd"
                                        clip-rule="evenodd"
                                        d="M2.5 5.83333C2.5 4.45262 3.61929 3.33333 5 3.33333H15C16.3807 3.33333 17.5 4.45262 17.5 5.83333V14.1667C17.5 15.5474 16.3807 16.6667 15 16.6667H5C3.61929 16.6667 2.5 15.5474 2.5 14.1667V5.83333ZM5 5H15C15.2487 5 15.4713 5.10528 15.6278 5.27639L10 9.55556L4.37222 5.27639C4.52869 5.10528 4.75127 5 5 5ZM4.16667 7.05556V14.1667C4.16667 14.6269 4.53976 15 5 15H15C15.4602 15 15.8333 14.6269 15.8333 14.1667V7.05556L10.4428 11.1481C10.1809 11.347 9.81908 11.347 9.55719 11.1481L4.16667 7.05556Z"
                                        fill="currentColor"
                                    />
                                </svg>
                                <span
                                    class="text-sm font-medium leading-5 text-slate-800 dark:text-slate-100"
                                >
                                    {{ userData.email }}
                                </span>
                                <span
                                    class="text-sm font-normal leading-5 text-slate-400 dark:text-slate-500"
                                >
                                    (your account)
                                </span>
                            </div>

                            <!-- Additional forwarding emails -->
                            <div
                                v-for="(emailItem, index) in forwardingEmails"
                                :key="emailItem.id"
                                class="flex py-3 px-0 items-center gap-3 self-stretch flex-nowrap"
                            >
                                <svg
                                    width="20"
                                    height="20"
                                    viewBox="0 0 20 20"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                    class="shrink-0 text-slate-600 dark:text-slate-400"
                                >
                                    <path
                                        fill-rule="evenodd"
                                        clip-rule="evenodd"
                                        d="M2.5 5.83333C2.5 4.45262 3.61929 3.33333 5 3.33333H15C16.3807 3.33333 17.5 4.45262 17.5 5.83333V14.1667C17.5 15.5474 16.3807 16.6667 15 16.6667H5C3.61929 16.6667 2.5 15.5474 2.5 14.1667V5.83333ZM5 5H15C15.2487 5 15.4713 5.10528 15.6278 5.27639L10 9.55556L4.37222 5.27639C4.52869 5.10528 4.75127 5 5 5ZM4.16667 7.05556V14.1667C4.16667 14.6269 4.53976 15 5 15H15C15.4602 15 15.8333 14.6269 15.8333 14.1667V7.05556L10.4428 11.1481C10.1809 11.347 9.81908 11.347 9.55719 11.1481L4.16667 7.05556Z"
                                        fill="currentColor"
                                    />
                                </svg>
                                <span
                                    class="text-sm font-medium leading-5 text-slate-800 dark:text-slate-100 truncate min-w-0"
                                >
                                    {{ emailItem.email }}
                                </span>
                                <!-- Pending badge -->
                                <div
                                    v-if="!emailItem.verified"
                                    class="flex px-2 py-0.5 justify-center items-center gap-1 rounded-full bg-slate-100 dark:bg-slate-700"
                                >
                                    <svg
                                        width="16"
                                        height="16"
                                        viewBox="0 0 16 16"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                        class="text-slate-500 dark:text-slate-400"
                                    >
                                        <path
                                            fill-rule="evenodd"
                                            clip-rule="evenodd"
                                            d="M8.00004 2.66683C5.05452 2.66683 2.66671 5.05464 2.66671 8.00016C2.66671 10.9457 5.05452 13.3335 8.00004 13.3335C10.9456 13.3335 13.3334 10.9457 13.3334 8.00016C13.3334 5.05464 10.9456 2.66683 8.00004 2.66683ZM1.33337 8.00016C1.33337 4.31826 4.31814 1.3335 8.00004 1.3335C11.6819 1.3335 14.6667 4.31826 14.6667 8.00016C14.6667 11.6821 11.6819 14.6668 8.00004 14.6668C4.31814 14.6668 1.33337 11.6821 1.33337 8.00016ZM8.00004 4.66683C8.36823 4.66683 8.66671 4.96531 8.66671 5.3335V7.72402L10.1381 9.19543C10.3985 9.45578 10.3985 9.87789 10.1381 10.1382C9.87776 10.3986 9.45565 10.3986 9.1953 10.1382L7.52864 8.47157C7.40361 8.34654 7.33337 8.17697 7.33337 8.00016V5.3335C7.33337 4.96531 7.63185 4.66683 8.00004 4.66683Z"
                                            fill="currentColor"
                                        />
                                    </svg>
                                    <span
                                        class="text-xs font-medium leading-4 text-slate-500 dark:text-slate-400"
                                    >
                                        Pending
                                    </span>
                                </div>
                                <div class="flex-1"></div>
                                <!-- Actions -->
                                <div class="flex items-center gap-4">
                                    <!-- Resend -->
                                    <button
                                        v-if="!emailItem.verified"
                                        @click="resendVerification(emailItem)"
                                        :disabled="resendingId === emailItem.id"
                                        class="flex items-center gap-2 text-sm font-medium leading-5 text-slate-800 dark:text-slate-100 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                                    >
                                        <svg
                                            width="20"
                                            height="20"
                                            viewBox="0 0 20 20"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                            class="shrink-0"
                                        >
                                            <g clip-path="url(#clip0_resend)">
                                                <path
                                                    fill-rule="evenodd"
                                                    clip-rule="evenodd"
                                                    d="M2.5 10C2.5 14.1421 5.85786 17.5 10 17.5C11.9017 17.5 13.6687 16.7913 15.0099 15.6213V16.6667C15.0099 17.1269 15.383 17.5 15.8432 17.5C16.3034 17.5 16.6765 17.1269 16.6765 16.6667V13.9583C16.6765 13.1529 16.0236 12.5 15.2182 12.5L12.5099 12.5C12.0496 12.5 11.6765 12.8731 11.6765 13.3333C11.6765 13.7936 12.0496 14.1667 12.5099 14.1667H14.131C13.0634 15.1965 11.5921 15.8333 10 15.8333C6.77834 15.8333 4.16667 13.2217 4.16667 10C4.16667 9.53976 3.79357 9.16667 3.33333 9.16667C2.8731 9.16667 2.5 9.53976 2.5 10ZM16.6667 10.8333C17.1269 10.8333 17.5 10.4602 17.5 10C17.5 5.85786 14.1421 2.5 10 2.5C8.10298 2.5 6.33993 3.2052 5 4.37007V3.33333C5 2.8731 4.6269 2.5 4.16667 2.5C3.70643 2.5 3.33333 2.8731 3.33333 3.33333L3.33333 6.04167C3.33333 6.84708 3.98625 7.5 4.79167 7.5H7.29167C7.7519 7.5 8.125 7.1269 8.125 6.66667C8.125 6.20643 7.7519 5.83333 7.29167 5.83333H5.86903C6.93664 4.80345 8.4079 4.16667 10 4.16667C13.2217 4.16667 15.8333 6.77834 15.8333 10C15.8333 10.4602 16.2064 10.8333 16.6667 10.8333Z"
                                                    fill="currentColor"
                                                />
                                            </g>
                                            <defs>
                                                <clipPath id="clip0_resend">
                                                    <rect width="20" height="20" fill="white" />
                                                </clipPath>
                                            </defs>
                                        </svg>
                                        <span class="hidden sm:inline">Resend</span>
                                    </button>
                                    <!-- Delete -->
                                    <button
                                        @click="confirmDeleteEmail(emailItem)"
                                        :disabled="removingId === emailItem.id"
                                        class="flex items-center gap-2 text-sm font-medium leading-5 text-slate-800 dark:text-slate-100 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                                    >
                                        <Icon
                                            icon="trash"
                                            :width="20"
                                            :height="20"
                                            svg-class="text-slate-800 dark:text-slate-100"
                                        />
                                        <span class="hidden sm:inline">Delete</span>
                                    </button>
                                </div>
                            </div>

                            <!-- Add email form (inline) -->
                            <div
                                v-if="showAddForm"
                                class="flex pt-2 items-center w-full"
                            >
                                <div class="flex items-start gap-2 flex-1">
                                    <input
                                        id="new-email"
                                        v-model="newEmail"
                                        type="email"
                                        placeholder="Enter email"
                                        class="flex-1 h-10 px-3 rounded border bg-white dark:bg-slate-800 text-sm font-normal leading-5 text-slate-800 dark:text-slate-100 placeholder:text-slate-500 dark:placeholder:text-slate-400 focus:outline-none focus:border-slate-400 dark:focus:border-slate-500"
                                        :class="emailError ? 'border-red-500' : 'border-slate-200 dark:border-slate-700'"
                                        @keyup.enter="addForwardingEmail"
                                        @input="emailError = ''"
                                    />
                                    <button
                                        @click="cancelAddForm"
                                        class="flex items-center justify-center w-10 h-10 shrink-0 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-800 dark:text-slate-100 transition-colors"
                                    >
                                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                                        </svg>
                                    </button>
                                    <button
                                        @click="addForwardingEmail"
                                        :disabled="!newEmail || addingEmail"
                                        class="flex items-center justify-center w-10 h-10 shrink-0 rounded bg-slate-800 dark:bg-slate-700 hover:bg-slate-700 dark:hover:bg-slate-600 disabled:opacity-50 transition-colors"
                                    >
                                        <svg v-if="!addingEmail" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M13.3334 4L6.00008 11.3333L2.66675 8" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                                        </svg>
                                        <i v-else class="fas fa-spinner fa-spin text-white text-sm"></i>
                                    </button>
                                </div>
                            </div>
                            <transition name="fade">
                                <div
                                    v-if="emailError"
                                    class="text-red-500 text-xs flex items-center gap-2 py-1.5"
                                    role="alert"
                                >
                                    <Icon icon="caution" />
                                    {{ emailError }}
                                </div>
                            </transition>

                            <!-- + Add email button -->
                            <div v-if="!showAddForm" class="py-3">
                                <button
                                    @click="showAddForm = true"
                                    class="flex items-center gap-2 text-sm font-medium leading-5 text-slate-800 dark:text-slate-100 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                                >
                                    <span class="text-base leading-none"
                                        >+</span
                                    >
                                    Add email
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- Delete confirmation modal -->
    <UiModal
        :is-open="showDeleteModal"
        @close="showDeleteModal = false"
        :is-teleported="true"
        width="600px"
        :full-screen-mobile="true"
    >
        <template #title>
            <div class="flex items-center">
                <div
                    class="w-12 h-12 rounded-full flex flex-col justify-center items-center mr-3 shrink-0 bg-red-100 dark:bg-red-900/30"
                >
                    <Icon
                        icon="trash"
                        path-class="fill-red-500"
                    />
                </div>
                <h5 class="flex-1 text-xl font-medium leading-7 text-slate-800 dark:text-slate-100">
                    Delete sender email
                </h5>
            </div>
        </template>
        <template #content>
            <div class="relative">
                <div class="space-y-4">
                    <p class="self-stretch text-sm font-normal leading-5 text-slate-800 dark:text-slate-200">
                        You are about to delete additional sender email. This action cannot be undone.
                    </p>
                </div>
            </div>
        </template>
        <template #footer>
            <div class="flex items-center justify-end gap-2">
                <button
                    type="button"
                    class="btn btn-secondary"
                    @click="showDeleteModal = false"
                >
                    Cancel
                </button>
                <button
                    type="button"
                    class="btn btn-danger"
                    @click="handleConfirmDelete"
                >
                    Delete email
                </button>
            </div>
        </template>
    </UiModal>
</template>

<script setup>
import Layout from '@/Layouts/Main.vue';
import { Head, usePage } from '@inertiajs/vue3';
import { ref, onMounted } from 'vue';
import SettingsNav from '@/Pages/Settings/partials/nav.vue';
import Icon from '@/components/ui/icon/icon.vue';
import UiModal from '@/components/ui/modal/ui-modal.vue';
import { Apii } from '@/services/api';
import { notify } from 'notiwind';

const ApiService = new Apii();
const page = usePage();

const props = defineProps({
    userData: Object,
    userSettings: Object,
    recentImports: {
        type: Array,
        default: () => [],
    },
    isPremium: {
        type: Boolean,
        default: false,
    },
});

defineOptions({
    layout: Layout,
});

const newEmail = ref('');
const addingEmail = ref(false);
const emailError = ref('');
const resendingId = ref(null);
const removingId = ref(null);
const showAddForm = ref(false);
const forwardingEmails = ref([]);
const showDeleteModal = ref(false);
const emailToDelete = ref(null);

const fetchForwardingEmails = async () => {
    try {
        const response = await ApiService.apiCall(
            '/user/forwarding-emails',
            'get'
        );
        const data = response.data || response;
        forwardingEmails.value = data.forwarding_emails || [];
    } catch (err) {
        console.error('Failed to fetch forwarding emails:', err);
    }
};

const showFlashMessages = () => {
    const flash = page.props.flash;
    if (flash?.success) {
        notify(
            {
                group: 'success',
                title: 'Success',
                text: flash.success,
            },
            4000
        );
    }
    if (flash?.error) {
        notify(
            {
                group: 'error',
                title: 'Error',
                text: flash.error,
            },
            4000
        );
    }
};

onMounted(() => {
    fetchForwardingEmails();
    showFlashMessages();
});

const cancelAddForm = () => {
    showAddForm.value = false;
    newEmail.value = '';
    emailError.value = '';
};

const addForwardingEmail = async () => {
    if (!newEmail.value) return;

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmail.value)) {
        emailError.value = 'Please enter a valid email address';
        return;
    }

    const existingEmail = forwardingEmails.value.find(
        (e) => e.email.toLowerCase() === newEmail.value.toLowerCase()
    );
    if (existingEmail) {
        emailError.value = 'This email is already registered';
        return;
    }

    emailError.value = '';
    addingEmail.value = true;

    try {
        const response = await ApiService.apiCall(
            '/user/forwarding-emails',
            'post',
            {
                email: newEmail.value.toLowerCase(),
            }
        );

        const data = response.data || response;
        if (data.forwarding_email) {
            forwardingEmails.value.push({
                ...data.forwarding_email,
                verified: false,
            });
        }

        newEmail.value = '';
        showAddForm.value = false;
        notify(
            {
                group: 'success',
                title: 'Verification Email Sent',
                text: 'Please check your inbox and click the verification link.',
            },
            4000
        );
    } catch (err) {
        const validationError = err.response?.data?.errors?.email?.[0];
        const customError = err.response?.data?.error;
        const errorMessage =
            validationError ||
            customError ||
            'Failed to add email. Please try again.';
        emailError.value = errorMessage;
    } finally {
        addingEmail.value = false;
    }
};

const resendVerification = async (emailItem) => {
    resendingId.value = emailItem.id;

    try {
        await ApiService.apiCall(
            `/user/forwarding-emails/${emailItem.id}/resend`,
            'post'
        );
        notify(
            {
                group: 'success',
                title: 'Verification Email Sent',
                text: `Verification email resent to ${emailItem.email}`,
            },
            4000
        );
    } catch (err) {
        notify(
            {
                group: 'error',
                title: 'Error',
                text: 'Failed to resend verification email. Please try again.',
            },
            4000
        );
    } finally {
        resendingId.value = null;
    }
};

const confirmDeleteEmail = (emailItem) => {
    emailToDelete.value = emailItem;
    showDeleteModal.value = true;
};

const handleConfirmDelete = () => {
    if (emailToDelete.value) {
        removeForwardingEmail(emailToDelete.value);
    }
    showDeleteModal.value = false;
    emailToDelete.value = null;
};

const removeForwardingEmail = async (emailItem) => {
    removingId.value = emailItem.id;

    try {
        await ApiService.apiCall(
            `/user/forwarding-emails/${emailItem.id}`,
            'delete'
        );
        forwardingEmails.value = forwardingEmails.value.filter(
            (e) => e.id !== emailItem.id
        );
        notify(
            {
                group: 'success',
                title: 'Sender email deleted',
                text: 'Additional sender email was successfully deleted.',
            },
            4000
        );
    } catch (err) {
        notify(
            {
                group: 'error',
                title: 'Error',
                text: 'Failed to remove email. Please try again.',
            },
            4000
        );
    } finally {
        removingId.value = null;
    }
};
</script>
